import React from 'react';
import './NewMainPage.css';
import BackgroundContainer from '../components/BackgroundContainer';
import MenuButton from '../components/MenuButton';
import BaseHeader from '../components/BaseHeader';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import { Button, Box, Typography } from '@mui/material';

// ─── Design 1: "Ember" ───────────────────────────────────────────────────────
// Warmer, slightly red-rose gradient. A touch moodier and more sensual.
const EmberButton = styled(Button)({
  fontFamily: 'Cormorant Garamond, serif',
  fontWeight: 400,
  fontSize: '1.15rem',
  letterSpacing: '0.1em',
  textTransform: 'none',
  color: '#e8c8c0',
  background: 'linear-gradient(180deg, rgba(82, 22, 32, 0.95) 0%, rgba(50, 10, 20, 0.98) 100%)',
  border: '1px solid rgba(200, 88, 100, 0.32)',
  borderRadius: '50px',
  padding: '14px 36px',
  minWidth: '65%',
  margin: '6px auto',
  display: 'flex',
  justifyContent: 'center',
  boxShadow: 'inset 0 1px 0 rgba(240, 180, 190, 0.08), 0 5px 22px rgba(0, 0, 0, 0.55)',
  transition: 'transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease',
  '&:hover': {
    transform: 'scale(1.025)',
    borderColor: 'rgba(215, 100, 115, 0.5)',
    boxShadow: 'inset 0 1px 0 rgba(245, 190, 200, 0.14), 0 0 20px rgba(180, 55, 70, 0.16), 0 6px 26px rgba(0, 0, 0, 0.6)',
  },
});

// ─── Design 2: "Dusk" ────────────────────────────────────────────────────────
// Almost-charcoal fill with just a rose undertone. The most understated —
// barely there until you focus on it. Quiet confidence.
const VelourButton = styled(Button)({
  fontFamily: 'Cormorant Garamond, serif',
  fontWeight: 600,
  fontSize: '1.1rem',
  letterSpacing: '0.1em',
  textTransform: 'none',
  color: '#e2c4cc',
  background: `linear-gradient(180deg, rgba(72, 22, 44, 0.92) 0%, rgba(44, 10, 28, 0.96) 100%), 
  radial-gradient(ellipse at 50% -20%, rgba(186, 151, 169, 0.28), transparent 72%)`,
  border: '1px solid rgba(175, 80, 115, 0.32)',
  borderRadius: '50px',
  padding: '14px 36px',
  minWidth: '65%',
  margin: '6px auto',
  display: 'flex',
  justifyContent: 'center',
  boxShadow: 'inset 0 1px 0 rgba(230, 170, 200, 0.1), 0 5px 22px rgba(0, 0, 0, 0.55)',
  transition: 'transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease',
  '&:hover': {
    transform: 'scale(1.025)',
    borderColor: 'rgba(205, 105, 140, 0.5)',
    boxShadow: 'inset 0 1px 0 rgba(240, 185, 210, 0.15), 0 7px 26px rgba(0, 0, 0, 0.62)',
  },
});

// ─── Design 3: "Bloom" ───────────────────────────────────────────────────────
// Richer rose fill with a permanent faint outer glow — has more presence.
// Still dark but with a visible warmth that draws the eye.
const LanternButton = styled(Button)({
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  fontSize: '1.2rem',
  letterSpacing: '0.09em',
  textTransform: 'none',
  color: '#d88cb2',
  background: `
    radial-gradient(ellipse at 50% -20%, rgba(203, 64, 136, 0.28), transparent 72%),
    linear-gradient(180deg, #2e1020 0%, #341121ff 55%, #6b2556ff 100%)
  `,
  border: '1px solid rgba(168, 58, 112, 0.3)',
  borderRadius: '50px',
  padding: '14px 36px',
  minWidth: '65%',
  margin: '6px auto',
  display: 'flex',
  justifyContent: 'center',
  boxShadow: '0 0 22px rgba(145, 42, 98, 0.2), 0 4px 16px rgba(0, 0, 0, 0.55)',
  transition: 'transform 0.22s ease, box-shadow 0.22s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    boxShadow: '0 0 32px rgba(160, 50, 110, 0.3), 0 5px 20px rgba(0, 0, 0, 0.62)',
  },
});

const DrinkButton = styled(Button)({
  position: 'relative',
  overflow: 'hidden',
  border: '1px solid rgba(160, 55, 110, 0.28)',
  background: `
    radial-gradient(ellipse at 50% 0%, rgba(160, 55, 120, 0.18), transparent 65%),
    linear-gradient(to bottom, #2e0f20 0%, #1e0916 55%, #160710 100%)
  `,
  color: '#d48aae',
  fontSize: '1.05rem',
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  letterSpacing: '0.09em',
  textTransform: 'none',
  borderRadius: '50px',
  padding: '13px 36px',
  marginTop: '10px',
  width: '75%',
  maxWidth: '300px',
  boxShadow: '0 0 18px rgba(140, 40, 95, 0.18), 0 4px 14px rgba(0, 0, 0, 0.55)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    boxShadow: '0 0 24px rgba(155, 50, 110, 0.28), 0 4px 16px rgba(0, 0, 0, 0.6)',
  },
});


const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '0.7rem',
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: 'rgba(180, 130, 155, 0.4)',
    marginBottom: '4px',
    marginTop: '20px',
    textAlign: 'center',
  }}>
    {children}
  </Typography>
);

function MainPageButtonTest() {
  const navigate = useNavigate();
  return (
    <BackgroundContainer sx={{ paddingBottom: '80px', overflowY: 'auto' }}>
      <MenuButton showLogin={true} showAccount={true} faq={true} showDownload={true} />
      <div className="menu-container">
        <BaseHeader>Deeper</BaseHeader>
        <p className="menu-subtitle">Explore yourselves, one question at a time.</p>

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>

          {/* <SectionLabel>Design 1 — Ember</SectionLabel> */}
          {/* <LanternButton onClick={() => navigate('/contestants')}>Play</LanternButton>
          <LanternButton onClick={() => navigate('/rules')}>Rules</LanternButton>
          <LanternButton onClick={() => navigate('/about')}>About</LanternButton> */}

          {/* <SectionLabel>Design 2 — Dusk</SectionLabel> */}
          {/* <VelourButton onClick={() => navigate('/contestants')}>Play</VelourButton>
          <VelourButton onClick={() => navigate('/rules')}>Rules</VelourButton>
          <VelourButton onClick={() => navigate('/about')}>About</VelourButton> */}

          <DrinkButton onClick={() => navigate('/contestants')}>Play</DrinkButton>
          <DrinkButton onClick={() => navigate('/rules')}>Rules</DrinkButton>
          <DrinkButton onClick={() => navigate('/about')}>About</DrinkButton>
{/* 
          <SectionLabel>Design 3 — Bloom</SectionLabel>
          <BloomButton onClick={() => navigate('/contestants')}>Play</BloomButton>
          <BloomButton onClick={() => navigate('/rules')}>Rules</BloomButton>
          <BloomButton onClick={() => navigate('/about')}>About</BloomButton> */}

        </Box>
      </div>
    </BackgroundContainer>
  );
}

export default MainPageButtonTest;
